import { useState, useEffect, useMemo, Dispatch, forwardRef, SetStateAction, useImperativeHandle } from 'react';
import moment from 'moment';
import {
  SpheraConfirmation,
  SpheraDropdownEx,
  SpheraCheckbox,
} from '@sphera-cloud/component-ui-reactjs';
import { Grid, GridSortChangeEvent, ISortDescriptor, ISpheraGridMetaData } from 'lca-common-ui-components';
import { File } from './types';
import { sendRequest, downloadFile, getFileContentByRangesRequest } from 'src/utilities/utilities';
import { useSpinnerContext } from 'src/context/SpinnerContext';
import FileDetailsModal from '../FileDetailsModal/FileDetailsModal';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { IFileSpecification } from 'src/store/slices/fileSpec/types';
import {
  fetchFiles,
  filesSelector,
  isLoadingSelector,
  isFileUploadedSelector,
  setIsFilesUpload,
  updateFile,
  deleteFile,
} from 'src/store/slices/files';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import FileInUseModal from '../FileInUseModal/FileInUseModal';
import { Job } from '../JobsList/interfaces';
import { useUserSettings } from 'src/hooks/useUserSettings';
import { ITEMS_PER_PAGE, PAGE_OPTIONS } from 'src/helpers/Constants';
import BulkOperationResultModal, { BulkOperationError, BulkOperationErrorLink } from './components/BulkOperationResultModal/BulkOperationResultModal';
import { TruncateText } from '../TruncateText';

interface Props {
  showNotification: (type: string, message: string) => void;
  types: IFileSpecification[];
  selectedFiles: Record<string, File>;
  setSelectedFiles: Dispatch<SetStateAction<Record<string, File>>>;
}

export interface Ref {
  removeSelectedFiles: () => void
}
interface FileWithSelection extends File {
  selected: boolean;
}

const DataItemKey = 'fileId';

const defaultFile = {
  fileId: '',
  name: '',
  mimeType: '',
  fileSize: 0,
  status: '',
  creationDatetime: '',
  lastChangeDatetime: '',
  expirationDateTime: '',
};

const initialPageOptions = {
  skip: 0,
  take: ITEMS_PER_PAGE,
};

const initialSort: Array<ISortDescriptor> = [{ field: '', dir: 'asc' }];

const FilesList = forwardRef(({ showNotification, types, selectedFiles, setSelectedFiles }: Props, ref) => {
  let timeout: ReturnType<typeof setTimeout> | number = 0;

  const dispatch = useAppDispatch();
  const { setShowSpinner } = useSpinnerContext();
  const [visible, setVisible] = useState(false);
  const [pageOptions, setPageOptions] = useState<{ skip: number; take: number }>(
    initialPageOptions,
  );
  const [sort, setSort] = useState(initialSort);
  const [selectedLine, setSelectedLine] = useState<File>(defaultFile);
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Succeeded');
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>();
  const { items: files, totalCount } = useAppSelector(filesSelector);
  const isLoadingFiles = useAppSelector(isLoadingSelector);
  const isFileUploaded = useAppSelector(isFileUploadedSelector);
  const [showJobsUsedDialog, setShowJobsUsedDialog] = useState<boolean>(false);
  const [jobsUsed, setJobsUsed] = useState<Job[]>([]);
  const [jobsUsedCount, setJobsUsedCount] = useState<number>(0);
  const { getFormattedDateTime, getFormattedExpirationDateTime, hasNoExpiringDate } = useUserSettings();
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState<boolean>(false);
  const [bulkOperationErrors, setBulkOperationErrors] = useState<Record<string, any[]>>({});
  const [showBulkOperationErrors, setShowBulkOperationErrors] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    removeSelectedFiles() {
      setBulkDeleteConfirmation(true);
    },
  }));

  const fileSpec = types.map((item) => {
    return { label: item.name, value: item.key, mimeType: item.mimeType };
  });
  const typeOptions = [{ label: '', value: '' }, ...fileSpec];
  const statusOptions = [
    { label: '', value: '' },
    { label: 'Succeeded', value: 'Succeeded' },
    { label: 'Running', value: 'Running' },
    { label: 'Pending', value: 'NotStarted' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Succeeded with issues ', value: 'SucceededWithIssues' },
  ];

  const loadFiles = () => {
    const { skip, take } = pageOptions;
    const page = skip / take + 1;
    const params = {
      take,
      page,
      name: '',
      mimetypes: typeFilter ? [typeFilter] : [],
      status: statusFilter ?? '',
      sort: '',
    };

    if (filter) {
      // @ts-ignore
      const nameFilter = filter?.filters.find((item) => item.field === 'name')?.value;
      if (nameFilter) params.name = nameFilter;
    }

    const sortFilter = sort[0]?.field;
    if (sortFilter) {
      params.sort = `${sortFilter} ${sort[0].dir}`;
    }

    return dispatch(fetchFiles(params));
  };

  const pageChange = (event: { page: { skip: number; take: number } }) => {
    const { skip, take } = event.page;
    setPageOptions({ skip, take });
  };

  const handleSortChange = (e: GridSortChangeEvent) => {
    if (pageOptions.skip !== initialPageOptions.skip) {
      setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    }
    setSort(e.sort as ISortDescriptor[]);
  };

  const fetchFileDetails = async (fileId: string) => {
    try {
      const endpoint = `files/${fileId}`;
      const response = await sendRequest(endpoint, 'GET', null, 'json', true);
      const retry = response.headers['retry-after'];

      return {
        data: response.data,
        retry: retry ? parseInt(retry, 10) * 1000 : 0,
      };

    } catch (e: any) {
      showNotification('error', e.message);
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const openModal = async (selectedFile: File): Promise<void> => {
    setVisible(true);
    setSelectedLine(selectedFile);
    let fileDetails: { data: File, retry: number } | undefined;
    do {
      fileDetails = await fetchFileDetails(selectedFile.fileId);
      if (fileDetails && fileDetails.retry > 0) {
        await new Promise(resolve => {
          timeout = setTimeout(resolve, fileDetails?.retry);
        });
      }
    } while (fileDetails && fileDetails.retry > 0);

    if (fileDetails) {
      setSelectedLine(fileDetails.data);
    }
  };

  const closeModal = () => {
    setVisible(false);
    setSelectedLine(defaultFile);
  };

  const openConfirmation = (file: File) => {
    setConfirmation(true);
    setSelectedLine(file);
  };

  const closeConfirmation = () => {
    setConfirmation(false);
    setSelectedLine(defaultFile);
    closeModal();
  };

  const fetchFileContent = async ({ fileId, name }: File) => {
    try {
      setShowSpinner(true);
      const customerId = sessionStorage.getItem('customerId');
      let endpoint = `files/${fileId}/content`;
      if (customerId) {
        endpoint = `files/${fileId}/content?customerId=${customerId}`;
      }
      const response = await getFileContentByRangesRequest(endpoint);
      downloadFile(response, name);
      setShowSpinner(false);
    } catch (e: any) {
      showNotification('error', e.message);
      setShowSpinner(false);
    }
  };

  const handleFetchFileContent = (file: File) => {
    fetchFileContent(file);
  };

  const updateFileExpiryDate = async (fileId: string, expireDate: string) => {
    const date = moment(expireDate).format('YYYY-MM-DDTHH:mm:ssZ');
    return dispatch(updateFile({ id: fileId, date, propertyName: 'expirationdate' }));
  };

  const deleteSingleFile = async ({ fileId }: File) => {
    setShowSpinner(true);
    try {
      const endpoint = `files/${fileId}`;
      const response = await sendRequest(endpoint, 'DELETE');
      if (response.length === 0) {
        loadFiles();
        closeConfirmation();
        showNotification('success', 'File removed successfully');
        setShowSpinner(false);
      } else {
        const json = JSON.parse(response);
        const jobs: Job[] = json.jobsUsed as Job[];
        setJobsUsed(jobs);
        setJobsUsedCount(json.count as number);
        setShowJobsUsedDialog(true);
        closeConfirmation();
        setShowSpinner(false);
      }
    } catch (e: any) {
      closeConfirmation();
      showNotification('error', e.message);
      setShowSpinner(false);
    }
  };

  const filterChange = (e: any) => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    setFilter(e?.filter);
  };

  const changeTypeFilter = (e: any) => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    setTypeFilter(e?.value.mimeType);
  };

  const changeStatusFilter = (e: any) => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    setStatusFilter(e?.value.value);
  };

  useEffect(() => {
    setShowSpinner(isLoadingFiles);
  }, [isLoadingFiles]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadFiles();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [pageOptions, filter, typeFilter, statusFilter, sort]);

  useEffect(() => {
    if (isFileUploaded) {
      loadFiles();
      dispatch(setIsFilesUpload(false));
    }
  }, [isFileUploaded]);

  const renderName = ({ dataItem }: { dataItem: File }) => {
    const name = dataItem.name;
    return <td>
      <TruncateText text={name ?? ''} />
    </td>;
  };

  const renderDate = ({ dataItem }: { dataItem: File }) => {
    const date = getFormattedDateTime(dataItem.creationDatetime);
    return <td>
      <TruncateText text={date ?? ''} />
    </td>;
  };

  const renderExpiresDate = ({ dataItem }: { dataItem: File }) => {
    const date = getFormattedExpirationDateTime(dataItem.expirationDate);
    return <td>
      <TruncateText text={date ?? ''} />
    </td>;
  };

  const renderType = ({ dataItem }: { dataItem: File }) => {
    const type = types.find((item) => item.mimeType === dataItem.mimeType)?.name;
    return <td>
      <TruncateText text={type ?? ''} />
    </td>;
  };

  const renderStatus = ({ dataItem }: { dataItem: File }) => {
    const status = dataItem.status;
    return <td>
      <TruncateText text={status ?? ''} />
    </td>;
  };

  const typeFilterCell = () => (
    <SpheraDropdownEx
      options={typeOptions}
      type="basic"
      onChange={changeTypeFilter}
      value={typeFilter}
      width={130}
    />
  );

  const statusFilterCell = () => (
    <SpheraDropdownEx
      options={statusOptions}
      type="basic"
      onChange={changeStatusFilter}
      value={statusFilter}
      width={130}
    />
  );

  const renderSelectRowCell = ({ dataItem }: { dataItem: FileWithSelection }) => {
    dataItem.selected = !!selectedFiles[dataItem.fileId];
    if (hasNoExpiringDate(dataItem.expirationDate)) {
      return (<td><SpheraCheckbox primary={true} value={false} disabled={true}></SpheraCheckbox></td>);
    } else {
      return (<td><SpheraCheckbox primary={true} value={dataItem.selected} onChange={(event: any) => {
        const newSelectedFiles = { ...selectedFiles };
        if (event.value) {
          newSelectedFiles[dataItem[DataItemKey]] = dataItem;
        } else {
          delete newSelectedFiles[dataItem[DataItemKey]];
        }
        setSelectedFiles(newSelectedFiles);

      }} /></td>);
    }
  };

  const metadata: ISpheraGridMetaData[] = useMemo(
    () => [
      {
        field: 'selected',
        title: '',
        width: '40px',
        type: 'component',
        object: '_selected',
        filterable: true,
        filterCell: 'custom',
        customFilter: <></>,
      },
      { field: 'name', title: 'Name', type: 'component', object: '_name', filterable: true, filterCell: 'text', sortable: true },
      { field: '', title: 'Uploaded In', type: 'component', object: '_uploadedIn', filterable: true, filterCell: 'custom', customFilter: <></>, sortable: true },
      { field: '', title: 'Expires On', type: 'component', object: '_expiresOn', filterable: true, filterCell: 'custom', customFilter: <></>, sortable: true },
      {
        field: 'fileType',
        title: 'File Type',
        type: 'component',
        object: '_fileType',
        width: '200px',
        filterable: true,
        filterCell: 'custom',
        customFilter: typeFilterCell(),
        sortable: true,
      },
      {
        field: 'status',
        title: 'Status',
        type: 'component',
        object: '_status',
        width: '200px',
        filterable: true,
        filterCell: 'custom',
        customFilter: statusFilterCell(),
        sortable: true,
      },
    ],
    [selectedFiles, getFormattedDateTime],
  );

  const actions = useMemo(
    () => [
      {
        text: 'Open File details',
        onClick: (selectedFile: File) => { openModal(selectedFile).then(r => console.log(r)); },
        icon: 'icon-ui-general-file',
      },
      {
        text: 'Download File',
        onClick: handleFetchFileContent,
        icon: 'icon-ui-general-download',
      },
      {
        text: 'Delete File',
        onClick: openConfirmation,
        icon: 'icon-ui-general-delete',
      },
    ],
    [],
  );

  const filesData = useMemo(
    () =>
      files.map((f) => {
        const fileActions = f.status === 'Succeeded' ? [...actions] : [actions[0], actions[2]];

        const row = {
          ...f,
          actions: fileActions,
          selected: !!selectedFiles[f.fileId],
        };

        return {
          ...row,
          _selected: (renderSelectRowCell({ dataItem: row as FileWithSelection }) as any)?.props?.children,
          _name: (renderName({ dataItem: row }) as any)?.props?.children,
          _uploadedIn: (renderDate({ dataItem: row }) as any)?.props?.children,
          _expiresOn: (renderExpiresDate({ dataItem: row }) as any)?.props?.children,
          _fileType: (renderType({ dataItem: row }) as any)?.props?.children,
          _status: (renderStatus({ dataItem: row }) as any)?.props?.children,
        };
      }),
    [files, sort, selectedFiles, types, getFormattedDateTime, getFormattedExpirationDateTime],
  );

  const updateFileExpiration = async (id: string, date: string) => {
    setShowSpinner(true);
    closeModal();
    await updateFileExpiryDate(id, date);
    await loadFiles();
    setShowSpinner(false);
  };

  const renderSelectedFiles = () => {
    const items = Object.values(selectedFiles ?? {});
    if (!items.length) return;
    return (
      <ul>
        {items.map(item => {
          return (<li key={item.fileId}><b>{item.name}</b></li>);
        })}
      </ul>
    );
  };

  const getBulkOperationErrorLink = (job: Job): BulkOperationErrorLink => {
    return {
      title: job.name,
      to: `/jobs/${job.jobId}`,
    };
  };

  const deleteFilesInBulk = async () => {
    setShowSpinner(true);
    const errors: Record<string, BulkOperationError[]> = {};
    const fileIds = Object.keys(selectedFiles);
    for (const fileId of fileIds) {
      const response = await dispatch(deleteFile(fileId));
      if (response.type.includes('rejected')) {
        const operationErrors: BulkOperationError[] = [];
        (response.payload as any).errors?.map((err: { messageResKey: string, message: string }) => {
          let errorMessage: string;
          let links: BulkOperationErrorLink[] = [];
          if (err.messageResKey === 'FILE_IN_USE') {
            const json = JSON.parse(err.message ?? "{}");
            errorMessage = `The file is in use by ${json.count} jobs:`;
            (json.jobsUsed as Job[])?.map(job => links.push(getBulkOperationErrorLink(job)));
          } else {
            errorMessage = err.message;
          }
          operationErrors.push({ message: errorMessage, links });
        });
        errors[fileId] = operationErrors;
      }
    }
    setShowSpinner(false);
    setBulkDeleteConfirmation(false);
    if (Object.keys(errors).length) {
      setBulkOperationErrors(errors);
      setShowBulkOperationErrors(true);
    } else {
      showNotification('success', 'The files were successfully deleted.');
      setSelectedFiles({});
      setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    }
  };

  return (
    <div className="file-list--table" id="file-list-table">
      {visible && (
        <FileDetailsModal
          closeModal={closeModal}
          details={selectedLine}
          downloadFile={fetchFileContent}
          deleteFile={openConfirmation}
          onAction={(isExpireMode: boolean, id: string, date: string) => isExpireMode ? updateFileExpiration(id, date) : closeModal()}
        />
      )}

      <Grid
        serverSideData
        pageable
        filterable
        maxAction={5}
        data={filesData}
        metadata={metadata}
        total={totalCount}
        actions={actions}
        sort={sort}
        onFilterChange={filterChange}
        onPageChange={pageChange}
        onSortChange={handleSortChange}
        pageOptions={PAGE_OPTIONS}
        {...pageOptions}
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: 'multiple',
        }}
        selectedField="selected"
        dataItemKey={DataItemKey}
      />
      {confirmation && (
        <SpheraConfirmation
          hasAction
          isDangerConfirm
          actionText="Delete"
          onCancel={() => closeConfirmation()}
          onAction={() => deleteSingleFile(selectedLine)}
          title="Delete this file"
        >
          Once you delete <b>{selectedLine.name}</b> file, it will be removed forever. Are you sure?
        </SpheraConfirmation>
      )}
      {showJobsUsedDialog && (
        <FileInUseModal
          onClose={() => setShowJobsUsedDialog(false)}
          jobsUsed={jobsUsed}
          count={jobsUsedCount}
        ></FileInUseModal>
      )}
      {bulkDeleteConfirmation && (
        <SpheraConfirmation
          hasAction
          isDangerConfirm
          actionText="Delete"
          onCancel={() => { setBulkDeleteConfirmation(false); }}
          onAction={() => deleteFilesInBulk()}
          title="Delete files"
        >
          Are you sure you want to delete selected files?
          {renderSelectedFiles()}
        </SpheraConfirmation>
      )}
      {showBulkOperationErrors &&
        <BulkOperationResultModal
          selectedFiles={selectedFiles}
          errors={bulkOperationErrors}
          onClose={() => {
            setSelectedFiles({});
            setPageOptions((prev) => ({ skip: 0, take: prev.take }));
            setShowBulkOperationErrors(false);
          }}
        />
      }
    </div >
  );
});

FilesList.displayName = 'FileList';

export default FilesList;
